import type { EventBus } from "../core/EventBus.ts";
import { COLORS } from "../core/constants.ts";
import { RARITY_SLOT_COLORS } from "../core/palette.ts";
import type { ScreenEffects } from "./ScreenEffects.ts";
import type { ParticleSystem } from "./ParticleSystem.ts";
import type { HitStopTier } from "../engine/GameLoop.ts";

interface AudioEngineLike {
  playSfx(name: string): void;
}

interface GameLoopLike {
  hitStop(frames: number): void;
  hitStopTier(tier: HitStopTier): void;
}

interface Shockwave {
  active: boolean;
  x: number;
  y: number;
  age: number; // ms
  duration: number; // ms
  maxRadius: number;
  color: string;
  lineWidth: number;
  implode: boolean;
}

const MAX_WAVES = 16;

/**
 * PayloadDetonationFx — render-only spectacle for payload detonation (PT-14).
 *
 * Listens to `payload.detonated { type, x, y }` and produces an analytic
 * expanding (or imploding) shockwave ring drawn in world space, plus a pooled
 * particle burst and an escalated screen punch. Never mutates sim state, never
 * uses the seeded RNG → determinism-safe. The ring is drawn before the flash
 * overlay and under the shake transform, so the WebGL bloom catches its glow.
 */
export class PayloadDetonationFx {
  private bus: EventBus;
  private screenEffects: ScreenEffects;
  private particles: ParticleSystem;
  private audioEngine: AudioEngineLike | null;
  private gameLoop_: GameLoopLike | null = null;
  private waves: Shockwave[] = [];
  private unsub: (() => void) | null = null;
  private unsubShrapnel: (() => void) | null = null;

  constructor(
    bus: EventBus,
    screenEffects: ScreenEffects,
    particleSystem: ParticleSystem,
    audioEngine?: AudioEngineLike,
  ) {
    this.bus = bus;
    this.screenEffects = screenEffects;
    this.particles = particleSystem;
    this.audioEngine = audioEngine ?? null;
    for (let i = 0; i < MAX_WAVES; i++) {
      this.waves.push({
        active: false,
        x: 0,
        y: 0,
        age: 0,
        duration: 0,
        maxRadius: 0,
        color: "#FFD700",
        lineWidth: 4,
        implode: false,
      });
    }
    this.unsub = this.bus.on("payload.detonated", (ev) => {
      this.onDetonated(ev as { type: string; x: number; y: number });
    });
    // Gold chain-reaction pops — one small gold micro-burst per trash that
    // detonates in the KERNEL PANIC cascade. Render-only; no shake/flash (the
    // main detonation owns the punch), so a dense cascade never over-juices.
    this.unsubShrapnel = this.bus.on("payload.shrapnel", (ev) => {
      this.onShrapnel(ev as { x: number; y: number });
    });
  }

  setGameLoop(gl: GameLoopLike): void {
    this.gameLoop_ = gl;
  }

  private onShrapnel(ev: { x: number; y: number }): void {
    this.spawn(ev.x, ev.y, 300, 64, "#FFE066", 3, false);
    this.particles.emit(ev.x, ev.y, "payload_gold", 8);
  }

  private spawn(
    x: number,
    y: number,
    duration: number,
    maxRadius: number,
    color: string,
    lineWidth: number,
    implode: boolean,
  ): void {
    const w = this.waves.find((s) => !s.active);
    if (!w) return; // budget exhausted — drop silently
    w.active = true;
    w.x = x;
    w.y = y;
    w.age = 0;
    w.duration = duration;
    w.maxRadius = maxRadius;
    w.color = color;
    w.lineWidth = lineWidth;
    w.implode = implode;
  }

  private onDetonated(ev: { type: string; x: number; y: number }): void {
    const { x, y } = ev;
    if (ev.type === "kernelPanic") {
      // Gold board-nuke: triple expanding rings + bright core + escalated punch.
      this.spawn(x, y, 640, 700, "#FFE066", 7, false);
      this.spawn(x, y, 520, 500, "#FFF4B0", 3, false);
      this.spawn(x, y, 900, 220, "#FFE066", 2.5, false);
      this.particles.emitRing(x, y, 40, "payload_gold", 26);
      this.particles.emitRing(x, y, 130, "payload_gold", 22);
      this.particles.emit(x, y, "payload_gold", 50);
      this.screenEffects.flash("#FFE066", 0.7);
      this.screenEffects.shake(24, 800);
      this.audioEngine?.playSfx("payload_kernelPanic");
      this.gameLoop_?.hitStopTier("payload"); // §6.8 payload tier (was hitStop(4))
    } else if (ev.type === "reduceMaxHp") {
      // Violet elite-expose implosion: twin collapsing rings + a brighter core.
      this.spawn(x, y, 520, 240, "#C77DFF", 6, true);
      this.spawn(x, y, 380, 140, "#E0AAFF", 3, true);
      this.particles.emitRing(x, y, 80, "payload_void", 26);
      this.particles.emit(x, y, "payload_void", 35);
      this.screenEffects.flash(RARITY_SLOT_COLORS.epic!, 0.55);
      this.screenEffects.shake(16, 550);
      this.audioEngine?.playSfx("payload_reduceMaxHp");
    } else if (ev.type === "gcForce") {
      // Green garbage-collection sweep: dissolving wave outward.
      this.spawn(x, y, 500, 500, COLORS.SENTINEL_GREEN, 4, false);
      this.spawn(x, y, 350, 300, "#7FFF7F", 2, false);
      this.particles.emitRing(x, y, 60, "payload_green", 20);
      this.particles.emit(x, y, "payload_green", 30);
      this.screenEffects.flash(COLORS.SENTINEL_GREEN, 0.4);
      this.screenEffects.shake(8, 400);
      this.audioEngine?.playSfx("payload_gcForce");
    } else if (ev.type === "logicBomb") {
      // Amber arming: small pulsing ring (the charge is planted).
      this.spawn(x, y, 400, 72, COLORS.AMBER_WARN, 3, false);
      this.particles.emit(x, y, "payload_amber", 10);
      this.audioEngine?.playSfx("payload_logicBomb_arm");
    } else if (ev.type === "logicBomb_explode") {
      // Amber cluster detonation: big ring + dense particles.
      this.spawn(x, y, 500, 200, COLORS.AMBER_WARN, 6, false);
      this.spawn(x, y, 350, 120, "#FFF4B0", 3, false);
      this.particles.emitRing(x, y, 50, "payload_amber", 22);
      this.particles.emit(x, y, "payload_amber", 40);
      this.screenEffects.flash(COLORS.AMBER_WARN, 0.5);
      this.screenEffects.shake(14, 500);
      this.audioEngine?.playSfx("payload_logicBomb");
      this.gameLoop_?.hitStopTier("short");
    } else if (ev.type === "backdoor") {
      // Cyan teleport suck-back: collapsing rings toward center.
      this.spawn(x, y, 600, 500, COLORS.ICE_CYAN, 5, true);
      this.spawn(x, y, 400, 300, "#80EAFF", 2.5, true);
      this.particles.emitRing(x, y, 100, "payload_cyan", 18);
      this.particles.emit(x, y, "payload_cyan", 25);
      this.screenEffects.flash(COLORS.ICE_CYAN, 0.45);
      this.screenEffects.shake(10, 500);
      this.audioEngine?.playSfx("payload_backdoor");
    } else if (ev.type === "forkBomb") {
      // Virus-green infected burst at target.
      this.spawn(x, y, 450, 120, "#00FF66", 4, false);
      this.spawn(x, y, 300, 80, "#99FFCC", 2, false);
      this.particles.emit(x, y, "payload_virus", 30);
      this.screenEffects.flash("#00FF66", 0.35);
      this.screenEffects.shake(6, 350);
      this.audioEngine?.playSfx("payload_forkBomb");
    } else if (ev.type === "pidHijack") {
      // Red mind-control burst: expanding red ring + particle shower.
      this.spawn(x, y, 550, 160, COLORS.ORACLE_RED, 5, false);
      this.spawn(x, y, 380, 100, "#FF6688", 2.5, false);
      this.particles.emitRing(x, y, 40, "payload_red", 16);
      this.particles.emit(x, y, "payload_red", 35);
      this.screenEffects.flash(COLORS.ORACLE_RED, 0.5);
      this.screenEffects.shake(12, 500);
      this.audioEngine?.playSfx("payload_pidHijack");
      this.gameLoop_?.hitStopTier("short");
    }
  }

  /** Advance shockwave ages. Called from the render pipeline update step. */
  update(dt: number): void {
    for (const w of this.waves) {
      if (!w.active) continue;
      w.age += dt;
      if (w.age >= w.duration) w.active = false;
    }
  }

  /** Draw active shockwaves in world space (additive glow). */
  render(ctx: CanvasRenderingContext2D): void {
    let any = false;
    for (const w of this.waves) {
      if (w.active) {
        any = true;
        break;
      }
    }
    if (!any) return;

    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const w of this.waves) {
      if (!w.active) continue;
      const t = w.age / w.duration;
      const eased = w.implode ? 1 - t : 1 - (1 - t) * (1 - t); // ease-out grow
      const radius = w.implode ? w.maxRadius * (1 - t) : w.maxRadius * eased;
      if (radius <= 0.5) continue;
      // Quick alpha ramp-in (first 12%) then fade to zero.
      const alpha = t < 0.12 ? t / 0.12 : 1 - (t - 0.12) / 0.88;
      ctx.globalAlpha = Math.max(0, alpha) * 0.9;
      ctx.strokeStyle = w.color;
      ctx.lineWidth = w.lineWidth * (1 - t * 0.6);
      ctx.beginPath();
      ctx.arc(w.x, w.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      // Faint wide halo for a softer bloom pickup.
      ctx.globalAlpha = Math.max(0, alpha) * 0.3;
      ctx.lineWidth = w.lineWidth * 2.5 * (1 - t * 0.4);
      ctx.stroke();
    }
    ctx.restore();
  }

  destroy(): void {
    this.unsub?.();
    this.unsub = null;
    this.unsubShrapnel?.();
    this.unsubShrapnel = null;
  }
}
